import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { ArrowLeft, AlertTriangle, Stethoscope, Globe, Phone } from 'lucide-react';
import { motion } from 'framer-motion';

export default function DisclaimerPage() {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="default" linkTo="/" />
          <div className="w-16 sm:w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A]">Medical Disclaimer</h1>
          </div>

          {/* Important Notice Banner */}
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
            <p className="text-amber-800 font-bold text-base sm:text-lg text-center mb-0">
              IMPORTANT: PLEASE READ CAREFULLY
            </p>
          </div>

          <div className="prose prose-lg max-w-none space-y-6 sm:space-y-8">
            {/* Not Medical Advice */}
            <section className="bg-white rounded-xl p-4 sm:p-6 border border-border">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0" />
                <h2 className="text-lg sm:text-xl font-semibold text-[#1A1A1A] m-0">Not Medical Advice</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed text-sm sm:text-base mb-0">
                <strong>Vitala.me is an informational tool, not a medical provider.</strong> The content 
                generated by our AI is intended for educational purposes regarding nutritional supplements only.
              </p>
            </section>

            {/* Consult Your Professionals */}
            <section className="bg-white rounded-xl p-4 sm:p-6 border border-border">
              <div className="flex items-center gap-3 mb-4">
                <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                <h2 className="text-lg sm:text-xl font-semibold text-[#1A1A1A] m-0">Consult Your Professionals</h2>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                  <p className="font-semibold text-blue-800 mb-2 text-sm sm:text-base">The Physician/Pharmacist Rule</p>
                  <p className="text-blue-700 text-xs sm:text-sm mb-0">
                    You <strong>must consult with a licensed physician or pharmacist</strong> before starting 
                    any new supplement regimen, especially if you have pre-existing conditions, are pregnant, 
                    or are taking prescription medications.
                  </p>
                </div>
                
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
                  <p className="font-semibold text-purple-800 mb-2 text-sm sm:text-base">Safety Checker</p>
                  <p className="text-purple-700 text-xs sm:text-sm mb-0">
                    Our AI Safety Checker is an <strong>assistive tool</strong> and does not replace 
                    the clinical judgment of a healthcare professional.
                  </p>
                </div>
              </div>
            </section>

            {/* Regulatory Disclosures */}
            <section className="bg-white rounded-xl p-4 sm:p-6 border border-border">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                <h2 className="text-lg sm:text-xl font-semibold text-[#1A1A1A] m-0">Regulatory Disclosures (Global)</h2>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
                  <p className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">FDA (USA)</p>
                  <p className="text-gray-700 text-xs sm:text-sm mb-0">
                    Statements regarding dietary supplements have not been evaluated by the FDA. 
                    These products are <strong>not intended to diagnose, treat, cure, or prevent any disease</strong>.
                  </p>
                </div>
                
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
                  <p className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">Global Standards</p>
                  <p className="text-gray-700 text-xs sm:text-sm mb-0">
                    We recommend users consult local health authorities (such as EFSA in Europe or TGA in Australia) 
                    regarding specific ingredient safety in their region.
                  </p>
                </div>
              </div>
            </section>

            {/* Emergency Situations */}
            <section className="bg-red-50 border-2 border-red-300 rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
                <h2 className="text-lg sm:text-xl font-bold text-red-800 m-0">Emergency Situations</h2>
              </div>
              <p className="text-red-700 font-medium mb-3 sm:mb-4 text-sm sm:text-base">
                DO NOT USE VITALA.ME FOR EMERGENCIES.
              </p>
              <p className="text-red-700 mb-3 sm:mb-4 text-sm sm:text-base">
                If you are experiencing a medical crisis, stop using this tool and contact your 
                local emergency services immediately:
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-4">
                <div className="bg-white rounded-lg px-3 sm:px-4 py-2 border border-red-200">
                  <span className="font-bold text-red-800 text-sm sm:text-base">911</span>
                  <span className="text-red-600 text-xs sm:text-sm ml-1 sm:ml-2">USA</span>
                </div>
                <div className="bg-white rounded-lg px-3 sm:px-4 py-2 border border-red-200">
                  <span className="font-bold text-red-800 text-sm sm:text-base">999</span>
                  <span className="text-red-600 text-xs sm:text-sm ml-1 sm:ml-2">UK</span>
                </div>
                <div className="bg-white rounded-lg px-3 sm:px-4 py-2 border border-red-200">
                  <span className="font-bold text-red-800 text-sm sm:text-base">112</span>
                  <span className="text-red-600 text-xs sm:text-sm ml-1 sm:ml-2">EU</span>
                </div>
                <a 
                  href="https://en.wikipedia.org/wiki/List_of_emergency_telephone_numbers" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-red-100 rounded-lg px-3 sm:px-4 py-2 border border-red-300 text-red-700 hover:bg-red-200 transition-colors text-xs sm:text-sm font-medium no-underline"
                >
                  View All Numbers
                </a>
              </div>
            </section>

            {/* Acceptance */}
            <section className="bg-primary/5 border border-primary/20 rounded-xl p-4 sm:p-6">
              <p className="text-[#1A1A1A] font-medium text-sm sm:text-base mb-0">
                By using Vitala.me, you acknowledge that you have read, understood, and agree to this disclaimer. 
                If you do not agree with any part of this disclaimer, please do not use our services.
              </p>
            </section>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
